import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '@/utils/api'

interface Feature {
  id: string
  feature_key: string
  enabled: boolean
  display_name: string
  description?: string
  category?: string
}

interface FeaturesContextType {
  features: Record<string, boolean>
  isFeatureEnabled: (featureKey: string) => boolean
  loading: boolean
  refresh: () => Promise<void>
}

const FeaturesContext = createContext<FeaturesContextType | undefined>(undefined)

export const FeaturesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [features, setFeatures] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)

  const loadFeatures = async () => {
    try {
      const data = await apiClient.get('/api/family/features')
      const featureMap: Record<string, boolean> = {}

      if (data.data && Array.isArray(data.data)) {
        data.data.forEach((feature: Feature) => {
          featureMap[feature.feature_key] = feature.enabled
        })
      }

      setFeatures(featureMap)
    } catch (error) {
      console.error('Failed to load features:', error)
      // On error, default to empty features (all disabled)
      setFeatures({})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFeatures()
  }, [])

  const isFeatureEnabled = (featureKey: string): boolean => {
    return features[featureKey] === true
  }

  const refresh = async () => {
    setLoading(true)
    await loadFeatures()
  }

  return (
    <FeaturesContext.Provider value={{ features, isFeatureEnabled, loading, refresh }}>
      {children}
    </FeaturesContext.Provider>
  )
}

export const useFeatures = () => {
  const context = useContext(FeaturesContext)
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeaturesProvider')
  }
  return context
}

// Helper hook for checking a single feature
export const useFeature = (featureKey: string): boolean => {
  const { isFeatureEnabled } = useFeatures()
  return isFeatureEnabled(featureKey)
}
